import React, { useMemo } from 'react';
import classnames from '../../utils/classnames.tsx';
import { AllowedTokens, Token, TokenList } from './TokenList.tsx';
import useExpression from './useExpression.ts';
import Button from '../Button.tsx';
import FormField from '../FormField.tsx';
import Field from '../Fields/Field.tsx';
import { withIcon } from '../Icon.tsx';
import { faArrowDown, faArrowUp, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import {
    DATETIME_ALLOWED_FUNCTIONS,
    DATETIME_ALLOWED_VARIABLES,
    DEFAULT_ALLOWED_OPERATORS,
    DEFAULT_ALLOWED_VARIABLES,
} from './constants.ts';
import { useToaster } from '../Toast.tsx';
import { type } from 'node:os';

const ArrowUpIcon = withIcon(faArrowUp);
const ArrowDownIcon = withIcon(faArrowDown);
const DeleteIcon = withIcon(faTrash);

const TokenField = ({
    label,
    value,
    onValueChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
}: AllowedTokens & {
    label: string;
    value: Token[];
    onValueChange: (tokens: Token[]) => void;
}) => {
    return (
        <FormField label={label} className="w-full">
            <Field>
                <Field.Input>
                    <TokenList
                        tokens={value}
                        onTokensChange={onValueChange}
                        className="p-1.5 flex-1 min-w-0 bg-surface-transparent tabular-nums outline-none flex-wrap"
                        allowedOperators={allowedOperators}
                        allowedVariables={allowedVariables}
                        allowedFunctions={allowedFunctions}
                        allowGroups={allowGroups}
                    />
                </Field.Input>
            </Field>
        </FormField>
    );
};

const Rule = ({
    className,
    rule,
    onRuleChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
    ...props
}: React.ComponentPropsWithRef<'div'> &
    AllowedTokens & {
        rule: { condition: Token[]; value: Token[] };
        onRuleChange: (rule: { condition: Token[]; value: Token[] }) => void;
    }) => {
    const { t } = useTranslation('core');

    return (
        <div
            className={classnames(
                'flex flex-col gap-4 rounded-md border border-line-weak shadow-sm p-4 pt-3.5 pb-4.5 min-w-0 flex-1',
                className
            )}
            {...props}
        >
            <TokenField
                label={t('components.rules.condition')}
                value={rule.condition}
                onValueChange={(tokens: Token[]) => onRuleChange({ ...rule, condition: tokens })}
                allowedOperators={allowedOperators}
                allowedVariables={allowedVariables}
                allowedFunctions={allowedFunctions}
                allowGroups={allowGroups}
            />
            <TokenField
                label={t('components.rules.value')}
                value={rule.value}
                onValueChange={(tokens: Token[]) => onRuleChange({ ...rule, value: tokens })}
                allowedOperators={allowedOperators}
                allowedVariables={allowedVariables}
                allowedFunctions={allowedFunctions}
                allowGroups={allowGroups}
            />
        </div>
    );
};

const RuleList = ({
    rules,
    onRulesChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    onCopy,
    onPaste,
}: AllowedTokens & {
    rules: { condition: Token[]; value: Token[] }[];
    onRulesChange: (rules: { condition: Token[]; value: Token[] }[]) => void;
    onCopy: () => void;
    onPaste: () => void;
}) => {
    const { t } = useTranslation('core');

    return (
        <div className="flex flex-col gap-4 w-full">
            {rules.map((rule, index) => (
                <div className="flex gap-2" key={index}>
                    <Rule
                        rule={rule}
                        onRuleChange={(newRule) => {
                            const newRules = [...rules];
                            newRules[index] = newRule;
                            onRulesChange(newRules);
                        }}
                        allowedOperators={allowedOperators}
                        allowedVariables={allowedVariables}
                        allowedFunctions={allowedFunctions}
                        allowGroups
                    />
                    <div className="flex flex-col justify-start gap-2">
                        <Button
                            className="hover:enabled:bg-surface-danger-weak text-content-danger-strong hover:enabled:text-content-danger-strong"
                            onClick={() => {
                                const newRules = rules.filter((_, i) => i !== index);
                                onRulesChange(newRules);
                            }}
                            type="button"
                        >
                            <DeleteIcon />
                        </Button>

                        <Button
                            onClick={() => {
                                if (index === 0) return;
                                const newRules = [...rules];
                                const temp = newRules[index - 1];
                                newRules[index - 1] = newRules[index];
                                newRules[index] = temp;
                                onRulesChange(newRules);
                            }}
                            type="button"
                        >
                            <ArrowUpIcon />
                        </Button>

                        <Button
                            onClick={() => {
                                if (index === rules.length - 1) return;
                                const newRules = [...rules];
                                const temp = newRules[index + 1];
                                newRules[index + 1] = newRules[index];
                                newRules[index] = temp;
                                onRulesChange(newRules);
                            }}
                            type="button"
                        >
                            <ArrowDownIcon />
                        </Button>
                    </div>
                </div>
            ))}

            <div className="flex gap-2 text-content-medium">
                <button
                    onClick={onCopy}
                    type="button"
                    className="text-content-brand-medium text-sm hover:underline cursor-pointer"
                >
                    {t('components.rules.copyButton')}
                </button>
                <span> - </span>
                <button
                    onClick={onPaste}
                    type="button"
                    className="text-content-brand-medium text-sm hover:underline cursor-pointer"
                >
                    {t('components.rules.pasteButton')}
                </button>
            </div>

            <Button onClick={() => onRulesChange([...rules, { condition: [], value: [] }])} type="button">
                {t('components.rules.addRuleButton')}
            </Button>

            <span className="text-content-medium text-xs">{t('components.rules.helpText')}</span>
        </div>
    );
};

export const RuleListField = ({
    value,
    onValueChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = DEFAULT_ALLOWED_OPERATORS,
}: AllowedTokens & {
    value: { condition: string; value: string }[];
    onValueChange: (value: { condition: string; value: string }[]) => void;
}) => {
    const { t } = useTranslation('core');
    const { publishToast } = useToaster();

    const { parse, serialize } = useExpression({
        allowedOperators,
        allowedVariables,
        allowedFunctions,
    });

    const parseRules = (rules: { condition: string; value: string }[]) => {
        return rules.map((rule) => ({
            condition: parse(rule.condition),
            value: parse(rule.value),
        }));
    };

    const serializeRules = (rules: { condition: Token[]; value: Token[] }[]) => {
        return rules.map((rule) => ({
            condition: serialize(rule.condition),
            value: serialize(rule.value),
        }));
    };

    const rules = useMemo(() => {
        try {
            return parseRules(value || []);
        } catch (e) {
            console.error(e);
            publishToast({
                status: 'error',
                description: t('components.rules.parseErrorToast'),
            });
            return [];
        }
    }, [value]);

    const handleRulesChange = (newRules: { condition: Token[]; value: Token[] }[]) => {
        onValueChange(serializeRules(newRules));
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(serializeRules(rules)));
        publishToast({
            status: 'success',
            description: t('components.rules.copySuccessToast'),
        });
    };

    const handlePaste = async () => {
        const text = await navigator.clipboard.readText();
        try {
            const serializedRules = JSON.parse(text);
            handleRulesChange([...rules, ...parseRules(serializedRules)]);
            publishToast({
                status: 'success',
                description: t('components.rules.pasteSuccessToast'),
            });
        } catch (e) {
            console.error(e);
            publishToast({
                status: 'error',
                description: t('components.rules.parseErrorToast'),
            });
        }
    };

    return (
        <RuleList
            rules={rules}
            onRulesChange={handleRulesChange}
            allowedVariables={allowedVariables}
            allowedFunctions={allowedFunctions}
            allowedOperators={allowedOperators}
            onCopy={handleCopy}
            onPaste={handlePaste}
        />
    );
};

export const DateRuleListField = (props: React.ComponentPropsWithRef<typeof RuleListField>) => {
    return (
        <RuleListField
            allowedVariables={[...DEFAULT_ALLOWED_VARIABLES, ...DATETIME_ALLOWED_VARIABLES]}
            allowedFunctions={DATETIME_ALLOWED_FUNCTIONS}
            {...props}
        />
    );
};
